import { WarningAmberOutlined as WarningAmberOutlinedIcon } from '@mui/icons-material';
import { Checkbox, FormControl, FormControlLabel, MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from "react";
import { ALLERGENS_PATH, DESSERT_DAYS_PATH, DISCOUNT_STAFF_PATH, MODEL_COMPLEX_PATH, PRICE_MEALS_EXTRA_PATH, SCHOOL_DATA_PATH, SIDE_DAYS_PATH, studentsPath, userOrdersPathDate } from "../../functions/src/admin/paths";
import { COMBO_ALWAYS, COMBO_DONT_USE, COMBO_WHEN_ALL_SELECTED, COMBO_WHEN_ANY_SELECTED, MAX_MEAL_ITEMS, PORTION_SIZE_MEDIUM, PORTION_SIZE_NORMAL, PORTION_SIZE_SMALL, SCHOOL_TYPE_NURSERY, SCHOOL_TYPE_PRIMARY, SCHOOL_TYPE_SECONDARY } from "../../functions/src/api/constants";
import { toDateFromServer } from "../../functions/src/api/dates";
import { canOrderMenuMealsForDay } from "../../functions/src/common/orderDeadlines";
import { filterMealsByDateAvailability } from '../../functions/src/common/mealAvailability';
import { getPersonnelPrice, getPrice, getPriceCombo, getPriceComboWithDiscount, getPriceDessert, getPriceDessertWithDiscount, getPriceSide, getPriceSideWithDiscount, getPriceWithDiscount } from "../../functions/src/common/pricing";
import AllergenPdfDownloadLink from "./AllergenPdfDownloadLink";
import { useComboMode, useCombosData } from "../api/comboUtils";
import { auth, DataSnapshot } from "../api/firebase";
import { useDatabase, useDatabaseVal, useList, useSnapshotState, useStorageUrl } from "../api/firebaseHooks";
import { useTopupDiscount } from "../api/funds";
import { uppercaseForLang, type Language } from "../api/langConfig";
import { getLangValue, langAsset, langContext } from "../api/Lang";
import { shouldShowPortionSize } from "../api/mealUtils";
import { usePortionSizeLabels } from "../api/portionUtils";
import { formatPriceEuro } from "../api/priceFormat";
import { useStrings } from "../common-strings";
import { Allergens, AllergensList, Meal, MealLine, ModelComplexity, OrderPerson, PriceMealsExtra, SchoolData } from "../types";
import { MealAllergensIndicator, MealPopupCategory, MealPopupFoodItemLayout, MEALS_POPUP_OVERLAY_Z_INDEX, getMealAllergenLabels } from "./mealPopupShared";
import { useOrderStrings } from "./order-strings";
import { useOrderDeadlineAt } from "./useOrderDeadlineAt";

export default function MealsPopUp({ date, menusSnapshot, dailyMealsList, typesList, recipient, schoolBreak, OrderMealsLists, actualSetMenusCount }: MealsPopUpProps) {

    const [lang] = useContext(langContext);
    const commonStrings = useStrings();
    const orderStrings = useOrderStrings();
    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);
    const deadlineAt = useOrderDeadlineAt();

    // Allergen PDF link — week of order date (hidden when file missing)
    const orderDate = useMemo(() => toDateFromServer(date), [date]);
    const { uid } = auth.currentUser;

    // Check if deadline for ordering menu meals has passed
    const canOrderMealMenu = !!deadlineAt && canOrderMenuMealsForDay(date, modelConstants, deadlineAt);

    // Check if recipient is staff or student using per-person flags
    const recipientData: DataSnapshot<OrderPerson> = useDatabase(`${studentsPath(uid)}/${recipient}`);
    const recipientPerson = recipientData?.val();
    const recipientIsStaff = !!recipientPerson?.personnel;
    const recipientIsStudent = !!recipientPerson?.student;

    // For allowMenu, we need to check if the current recipient is staff/parent
    // If recipient is staff, treat as staff ordering; if recipient is student, treat as parent ordering
    const allowMenu = (recipientIsStudent && modelConstants?.allowPackageMealsParents) || (recipientIsStaff && modelConstants?.allowPackageMealsStaff);

    const OrderMealsListsBreak = OrderMealsLists?.filter(orderPerBreak => orderPerBreak.child('schoolBreak').val() === schoolBreak);
    const hasPackageInOrder = OrderMealsListsBreak?.length > 0 && OrderMealsListsBreak.some(oml => oml.child('isPackage').val() && oml.child('recipient').val() === recipient);
    const filteredSnapshot = OrderMealsListsBreak.filter(sMeal => sMeal.child('recipient').val() === recipient && !sMeal.child('isPackage').val());

    const loading = !menusSnapshot;
    const list = useList(menusSnapshot);

    const numOfSetMenus = list?.filter(sMeal => sMeal.key != 'sideMenu' && sMeal.key != 'dessertMenu')?.length;
    const hasSetMenu = numOfSetMenus > 0;

    const sideDish: Meal = menusSnapshot?.child('sideMenu')?.val();
    const dessert: Meal = menusSnapshot?.child('dessertMenu')?.val();

    // Check if there are any meals available for the "Everyday Choices" section
    // This section only shows meals that are NOT main, sideMenu, or dessertMenu (those are in the Set Menu section)
    const hasEverydayMealsAvailable = useMemo(() => {
        return typesList.some(sType => {
            if (sType.child('order').val() <= 0 || sType.key === 'raw' || sType.key === 'main' || sType.key === 'sideMenu' || sType.key === 'dessertMenu')
                return false;

            const courseTypeFiltered = dailyMealsList?.filter(sMeal => sMeal.child('courseType').val() === sType.key && sMeal.child('order')?.val() >= 0) || [];

            return filterMealsByDateAvailability(courseTypeFiltered, date)?.length > 0;
        });
    }, [typesList, dailyMealsList, date]);

    const setMenuItems = useMemo(
        () => list?.filter(sMeal => sMeal.key != 'sideMenu' && sMeal.key != 'dessertMenu') ?? [],
        [list],
    );

    const lastEverydayMealKey = useMemo(() => {
        let lastKey: string | undefined;
        typesList.filter(s_type => s_type.child('order').val() > 0).forEach(sType => {
            if (sType.key === 'raw' || sType.key === 'main' || sType.key === 'sideMenu' || sType.key === 'dessertMenu')
                return;
            const courseTypeFiltered = dailyMealsList?.filter(sMeal => sMeal.child('courseType').val() === sType.key && sMeal.child('order')?.val() >= 0) || [];
            const filteredMeals = filterMealsByDateAvailability(courseTypeFiltered, date);
            if (filteredMeals.length > 0)
                lastKey = filteredMeals[filteredMeals.length - 1].key;
        });
        return lastKey;
    }, [typesList, dailyMealsList, date]);

    return <div className="meals-popup" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        {/* Gefsinus Logo */}
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '20px'
        }}>
            <img
                src={langAsset('gefsinusLogoDark', lang)}
                alt="Gefsinus"
                style={{
                    maxHeight: '80px',
                    maxWidth: '140px',
                    objectFit: 'contain'
                }}
            />
        </div>

        {hasPackageInOrder &&
            <div className="cancel-order-box" style={{ marginBottom: '20px' }}>
                <img className="order-info-alert-icon" src="/img-assets/info.svg" alt="" />
                <div className='cancel-order-box-text'>
                    <span>
                        {orderStrings.mealsPopupPackageMealLine1}
                        <br />
                        {orderStrings.mealsPopupPackageMealLine2}
                        <br />
                        {orderStrings.mealsPopupPackageMealLine3}
                    </span>
                </div>
            </div>
        }
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className={'popup-heading-meals'}>{orderStrings.mealsPopupSetMenusHeading}</div>
            <AllergenPdfDownloadLink
                lang={lang}
                targetDate={orderDate}
                label={orderStrings.mealsPopupAllergensList}
                style={{ marginTop: '32px' }}
            />
        </div>
        {!allowMenu && !hasPackageInOrder && <div className={'menu-description'} style={{ color: 'var(--red_error)' }}>{orderStrings.mealsPopupPackageOnlyNote}</div>}
        <div className={'menu-description'}>
            {actualSetMenusCount === 1 ? (
                orderStrings.mealsPopupOneSetMenuDescription
            ) : (
                <>{orderStrings.mealsPopupSetMenusOfferPrefix}{actualSetMenusCount}{orderStrings.mealsPopupSetMenusOfferSuffix}</>
            )}
        </div>

        {/* Show deadline message when menu meals deadline has passed */}
        {!canOrderMealMenu && hasSetMenu && (
            <MealsPopupSetMenuCallout
                lang={lang}
                variant="menu-deadline"
                eyebrow={orderStrings.mealsPopupSetMenuCalloutEyebrow}
                title={orderStrings.mealsPopupMenuDeadlineTitle}
                detail={orderStrings.mealsPopupMenuDeadlineDetail}
            />
        )}

        {loading && commonStrings.loading
            || !hasSetMenu &&
            <MealsPopupSetMenuCallout
                lang={lang}
                variant="menu-unavailable"
                eyebrow={orderStrings.mealsPopupSetMenuCalloutEyebrow}
                title={orderStrings.mealsPopupSetMenusNotYetAvailable}
            />
            || setMenuItems.map((listKey, index) =>
                <MealPopUpLine key={listKey.key}
                    date={date}
                    mealKey={listKey.child('key').val()}
                    meal={listKey.val()}
                    recipient={recipient}
                    schoolBreak={schoolBreak}
                    qSnapshot={filteredSnapshot.filter(sMeal => sMeal.child('key').val() === listKey.child('key').val())[0]}
                    sideDish={sideDish}
                    dessert={dessert}
                    hideBottomBorder={hasEverydayMealsAvailable && index === setMenuItems.length - 1}
                />)
        }


        {hasEverydayMealsAvailable && <>
            <div className={'divider-line'}></div>
            <div style={{ marginTop: '20px' }}>
                <div className='popup-heading-meals' style={{ marginTop: '0px' }}>{orderStrings.mealsPopupEverydayChoicesHeading}</div>
            </div>
            <div className={'menu-description'}>{orderStrings.mealsPopupEverydayChoicesDescription}</div>

            {
                typesList.filter(s_type => s_type.child('order').val() > 0).map(sType => {
                    // This section only shows meals that are NOT main, sideMenu, or dessertMenu
                    // All course types here use dailyMealsList (from dailyMeals path)
                    const courseTypeFiltered = dailyMealsList?.filter(sMeal => sMeal.child('courseType').val() === sType.key && sMeal.child('order')?.val() >= 0) || [];
                    // Filter by date availability
                    const filteredMeals = filterMealsByDateAvailability(courseTypeFiltered, date);

                    return <React.Fragment key={sType.key} >
                        {sType.key != 'raw' && <>
                            {filteredMeals?.length > 0 && <MealPopupCategory heading={sType.child(lang).val()} subHeading={sType.child("description").child(lang).val()} />}
                            {filteredMeals.map(s_line => <MealPopUpLine key={s_line.key}
                                date={date}
                                mealKey={s_line.key}
                                meal={s_line.val()}
                                recipient={recipient}
                                schoolBreak={schoolBreak}
                                qSnapshot={filteredSnapshot.filter(sMeal => sMeal.child('key').val() === s_line.key)[0]}
                                sideDish={null}
                                dessert={null}
                                hideBottomBorder={s_line.key === lastEverydayMealKey}
                            />
                            )}

                        </>}
                    </React.Fragment>
                }
                )
            }
        </>
        }
    </div>
}

type MealsPopupSetMenuCalloutVariant = 'menu-deadline' | 'menu-unavailable';

type MealsPopupSetMenuCalloutProps = {
    lang: Language;
    variant: MealsPopupSetMenuCalloutVariant;
    eyebrow: string;
    title: string;
    detail?: string;
};

function MealsPopupSetMenuCallout({ lang, variant, eyebrow, title, detail }: MealsPopupSetMenuCalloutProps) {
    return (
        <div className={`changing-order-callout changing-order-callout--${variant}`} role="status">
            <div className="changing-order-callout__icon" aria-hidden>
                <WarningAmberOutlinedIcon fontSize="small" />
            </div>
            <div className="changing-order-callout__text">
                <p className="changing-order-callout__eyebrow">{uppercaseForLang(eyebrow, lang)}</p>
                <p className="changing-order-callout__title">{title}</p>
                {!!detail && <p className="changing-order-callout__detail">{detail}</p>}
            </div>
        </div>
    );
}

type MealsPopUpProps = {
    date: string
    menusSnapshot: DataSnapshot;
    dailyMealsList: DataSnapshot<Meal>[];
    typesList: DataSnapshot[];
    recipient: string;
    schoolBreak: string;
    OrderMealsLists: DataSnapshot[]; // Meals of order per recipient and time
    actualSetMenusCount: number; // Corrected count excluding side dishes and desserts
}

function MealPopUpLine({ date, mealKey, meal, recipient, schoolBreak, qSnapshot, sideDish, dessert, hideBottomBorder }: MealPopUpLineProps) {

    const [lang] = useContext(langContext);
    const commonStrings = useStrings();
    const orderStrings = useOrderStrings();
    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);
    const deadlineAt = useOrderDeadlineAt();
    const [schoolData] = useDatabaseVal<SchoolData>(SCHOOL_DATA_PATH);

    // Convert Firebase Storage URL to download URL
    const imageUrl = useStorageUrl(meal?.imgUrl);

    const [quantity, setQuantity] = useSnapshotState<number>(qSnapshot, 'quantity');
    const [quantitySide, setQuantitySide] = useSnapshotState<number>(qSnapshot, 'quantitySide');
    const [quantityDessert, setQuantityDessert] = useSnapshotState<number>(qSnapshot, 'quantityDessert');

    const { uid } = auth.currentUser;

    // Get active top-up discount
    const topupDiscount = useTopupDiscount();
    const discountPercentage = topupDiscount?.discountPercentage || 0;

    // Check if recipient is staff or student using per-person flags
    const recipientData: DataSnapshot<OrderPerson> = useDatabase(`${studentsPath(uid)}/${recipient}`);
    const recipientPerson = recipientData?.val();
    const recipientIsStaff = !!recipientPerson?.personnel;
    const recipientIsStudent = !!recipientPerson?.student;

    const isPackage: boolean = qSnapshot?.child('isPackage')?.val() || false;

    const staffDiscount: number = useDatabase(DISCOUNT_STAFF_PATH)?.val() || 0;

    const isSetMenu = meal.courseType === 'main';

    const dayNum = toDateFromServer(date).getDay();

    const mealSide = meal?.sideDish?.[lang];
    const sideDay: string = useDatabase(`${SIDE_DAYS_PATH}/${dayNum}/${lang}`)?.val() ?? '';
    const sideMenu: string = !sideDish ? '' : getLangValue(sideDish.name, lang);
    const sideName: string = !mealSide ? !sideMenu ? sideDay : sideMenu : mealSide;
    // const sideName: string = meal?.preventSide ? null : !mealSide ? !sideMenu ? sideDay : sideMenu : mealSide;

    const dessertDay: string = useDatabase(`${DESSERT_DAYS_PATH}/${dayNum}/${lang}`)?.val() ?? '';
    const dessertMenu: string = !dessert ? '' : getLangValue(dessert.name, lang);
    const dessertName: string = !dessertMenu ? dessertDay : dessertMenu;
    // const dessertName: string = meal?.preventDessert ? null : !dessertMenu ? dessertDay : dessertMenu;

    // Combo logic
    const comboSetting = useComboMode(recipientIsStaff) ?? COMBO_DONT_USE;
    const combosData = useCombosData();
    const isComboAlways = comboSetting === COMBO_ALWAYS;
    const isComboWhenAllSelected = comboSetting === COMBO_WHEN_ALL_SELECTED;
    const isComboWhenAnySelected = comboSetting === COMBO_WHEN_ANY_SELECTED;
    const isComboEnabled = isComboAlways || isComboWhenAllSelected || isComboWhenAnySelected;

    // Pending side/dessert selections before a meal quantity is added (no order line yet)
    const [pendingSideSelected, setPendingSideSelected] = useState(false);
    const [pendingDessertSelected, setPendingDessertSelected] = useState(false);

    useEffect(() => {
        if (isComboAlways) {
            if (!!sideName) setPendingSideSelected(true);
            if (!!dessertName) setPendingDessertSelected(true);
        }
    }, [isComboAlways, sideName, dessertName]);

    const sideSelected = quantity > 0 ? quantitySide > 0 : pendingSideSelected;
    const dessertSelected = quantity > 0 ? quantityDessert > 0 : pendingDessertSelected;

    // Check if all items are selected for combo when all selected
    const allItemsSelected = sideSelected && dessertSelected;
    // Check if any items are selected for combo when any selected
    const anyItemsSelected = sideSelected || dessertSelected;
    const shouldUseComboPrice = isSetMenu && (isComboAlways || (isComboWhenAllSelected && allItemsSelected) || (isComboWhenAnySelected && anyItemsSelected));

    const canOrderMealMenu = !isSetMenu || (!!deadlineAt && canOrderMenuMealsForDay(date, modelConstants, deadlineAt)); //|| !isPackage;
    const canOrderSetMenu = !isSetMenu || (modelConstants?.allowPackageMealsParents && recipientIsStudent) || (modelConstants?.allowPackageMealsStaff && recipientIsStaff) || isPackage;
    const cannotOrderMealSetMenu = (!canOrderMealMenu || !canOrderSetMenu);

    const isSetMenuForStudent = recipientIsStudent && isSetMenu;
    const hasStaffComboPrice = shouldUseComboPrice && (!!meal?.priceComboStaff || !!combosData?.prices?.priceStaff);
    const isStaffDiscountable = recipientIsStaff && staffDiscount > 0 && (isSetMenu || meal.isExchangable || meal.applyStaffDiscount) && !hasStaffComboPrice;

    const mealLine: MealLine = qSnapshot?.val();

    // Check if preferGlobalComboPrices is enabled
    const preferGlobalComboPrices = combosData?.prices?.preferGlobalComboPrices ?? false;

    const hasSmallPortion = shouldUseComboPrice ?
        (preferGlobalComboPrices || !meal?.priceComboSmall ? (!!combosData?.prices?.priceSmall && combosData.prices.priceSmall > 0) : (!!meal?.priceComboSmall && meal?.priceComboSmall > 0)) :
        (!!meal?.priceSmall && meal?.priceSmall > 0);
    const hasMediumPortion = shouldUseComboPrice ?
        (preferGlobalComboPrices || !meal?.priceComboMedium ? (!!combosData?.prices?.priceMedium && combosData.prices.priceMedium > 0) : (!!meal?.priceComboMedium && meal?.priceComboMedium > 0)) :
        (!!meal?.priceMedium && meal?.priceMedium > 0);
    const hasNormalPortion = shouldUseComboPrice ?
        (preferGlobalComboPrices || !meal?.priceComboLarge ? (!!combosData?.prices?.priceLarge && combosData.prices.priceLarge > 0) : (!!meal?.priceComboLarge && meal?.priceComboLarge > 0)) :
        (!!meal?.price && meal?.price > 0);
    const hasMorePortionSizes = (hasSmallPortion && hasMediumPortion) || (hasMediumPortion && hasNormalPortion) || (hasNormalPortion && hasSmallPortion);

    // Check if we should show portion size dropdown (hide only if there's only large portion available)
    // Pass the calculated portion availability flags to handle combo pricing for set menus
    const shouldShowPortionSizeValue = shouldShowPortionSize(meal, combosData, hasSmallPortion, hasMediumPortion, hasNormalPortion);
    const portionCount = [hasSmallPortion, hasMediumPortion, hasNormalPortion].filter(Boolean).length;
    const allowChangePortions = modelConstants?.allowChangePortions && (portionCount > 1) && !recipientIsStaff;

    // Auto-set side and dessert quantities when combo pricing is available and meal is first added
    React.useEffect(() => {
        if (quantity > 0 && isSetMenu && isComboAlways) {
            // Only auto-select if they haven't been manually set yet (undefined or null) and if this is the first time adding the meal
            if ((quantitySide === undefined || quantitySide === null) && !!sideName) {
                qSnapshot?.ref.child('quantitySide').set(quantity);
            }
            if ((quantityDessert === undefined || quantityDessert === null) && !!dessertName) {
                qSnapshot?.ref.child('quantityDessert').set(quantity);
            }
        }
    }, [quantity, isSetMenu, isComboAlways, sideName, dessertName, qSnapshot?.ref]);

    // Handle "When any selected" combo mode - treat sideMenu and dessertMenu as one unit
    React.useEffect(() => {
        if (quantity > 0 && isSetMenu && isComboWhenAnySelected && !!sideName && !!dessertName) {
            // If either side or dessert is selected, ensure both are selected with the same quantity
            if (quantitySide > 0 && quantityDessert === 0) {
                qSnapshot?.ref.child('quantityDessert').set(quantitySide);
            } else if (quantityDessert > 0 && quantitySide === 0) {
                qSnapshot?.ref.child('quantitySide').set(quantityDessert);
            }
        }
    }, [quantity, isSetMenu, isComboWhenAnySelected, quantitySide, quantityDessert, sideName, dessertName, qSnapshot?.ref]);

    const [customPortion, setCustomPortion] = useState<number>(null);

    const { small: smallPortionName, medium: mediumPortionName, large: normalPortionName } = usePortionSizeLabels();

    const portionSize = useMemo(() => {
        if (!!mealLine?.portionSize)
            return mealLine.portionSize;
        if (!!customPortion && customPortion > 0)
            return customPortion;
        if (recipientIsStaff)
            return hasNormalPortion ? PORTION_SIZE_NORMAL : hasMediumPortion ? PORTION_SIZE_MEDIUM : hasSmallPortion ? PORTION_SIZE_SMALL : PORTION_SIZE_NORMAL;

        // for students, portion size depends on their age, but only if available
        if (schoolData?.schoolType === SCHOOL_TYPE_NURSERY)
            return hasSmallPortion ? PORTION_SIZE_SMALL : hasMediumPortion ? PORTION_SIZE_MEDIUM : hasNormalPortion ? PORTION_SIZE_NORMAL : PORTION_SIZE_SMALL;
        if (schoolData?.schoolType === SCHOOL_TYPE_PRIMARY)
            return hasMediumPortion ? PORTION_SIZE_MEDIUM : hasSmallPortion ? PORTION_SIZE_SMALL : hasNormalPortion ? PORTION_SIZE_NORMAL : PORTION_SIZE_MEDIUM;
        if (schoolData?.schoolType === SCHOOL_TYPE_SECONDARY)
            return hasNormalPortion ? PORTION_SIZE_NORMAL : hasMediumPortion ? PORTION_SIZE_MEDIUM : hasSmallPortion ? PORTION_SIZE_SMALL : PORTION_SIZE_NORMAL;

        return hasNormalPortion ? PORTION_SIZE_NORMAL : hasMediumPortion ? PORTION_SIZE_MEDIUM : hasSmallPortion ? PORTION_SIZE_SMALL : PORTION_SIZE_NORMAL;
    }, [mealLine, customPortion, recipientIsStaff, schoolData, hasSmallPortion, hasMediumPortion, hasNormalPortion]);

    const mealPrice = useMemo(() => {
        // Calculate meal price with appropriate discounts (same logic as Order.tsx and pricingUtils)
        let finalPrice: number;
        if (!isStaffDiscountable) {
            // Students or staff without staff discount: apply topup discount
            finalPrice = isSetMenu && shouldUseComboPrice ?
                getPriceComboWithDiscount(portionSize, meal, combosData, discountPercentage, recipientIsStaff) :
                getPriceWithDiscount(portionSize, meal?.priceSmall, meal?.priceMedium, meal?.price, discountPercentage);
        } else {
            // Staff with staff discount: only apply staff discount, no topup discount
            const basePrice = isSetMenu && shouldUseComboPrice ?
                getPriceCombo(portionSize, meal, combosData, recipientIsStaff) :
                getPrice(portionSize, meal?.priceSmall, meal?.priceMedium, meal?.price);
            finalPrice = getPersonnelPrice(basePrice, staffDiscount);
        }
        return finalPrice;
    }, [meal, portionSize, shouldUseComboPrice, combosData, discountPercentage, recipientIsStaff, isStaffDiscountable, staffDiscount, isSetMenu]);

    const [pricesExtra] = useDatabaseVal<PriceMealsExtra>(PRICE_MEALS_EXTRA_PATH);

    const priceSideStr = useMemo(() => {
        const originalPrice = getPriceSide(portionSize, pricesExtra);
        const discountedPrice = getPriceSideWithDiscount(portionSize, pricesExtra, discountPercentage);

        if (discountPercentage > 0 && originalPrice > 0) {
            return `${formatPriceEuro(originalPrice, lang)} - ${formatPriceEuro(discountedPrice, lang)}`;
        }
        return discountedPrice > 0 ? formatPriceEuro(discountedPrice, lang) : formatPriceEuro(0, lang);
    }, [pricesExtra, portionSize, discountPercentage]);
    const priceSideName = !sideDish?.price ? priceSideStr : formatPriceEuro(Number(sideDish.price), lang);

    const priceDessertStr = useMemo(() => {
        const originalPrice = getPriceDessert(portionSize, pricesExtra);
        const discountedPrice = getPriceDessertWithDiscount(portionSize, pricesExtra, discountPercentage);

        if (discountPercentage > 0 && originalPrice > 0) {
            return `${formatPriceEuro(originalPrice, lang)} - ${formatPriceEuro(discountedPrice, lang)}`;
        }
        return discountedPrice > 0 ? formatPriceEuro(discountedPrice, lang) : formatPriceEuro(0, lang);
    }, [pricesExtra, portionSize, discountPercentage]);
    const priceDessertName = !dessert?.price ? priceDessertStr : formatPriceEuro(Number(dessert.price), lang);

    // Hide individual prices when combo is active or when "When any selected" mode is enabled
    const shouldHideIndividualPrices = isSetMenu && (shouldUseComboPrice || isComboWhenAnySelected);

    // Create side dish display with crossed-out pricing when discount exists
    const displaySideName = shouldHideIndividualPrices ? sideName : (() => {
        if (discountPercentage > 0 && !sideDish?.price) {
            const originalPrice = getPriceSide(portionSize, pricesExtra);
            const discountedPrice = getPriceSideWithDiscount(portionSize, pricesExtra, discountPercentage);
            if (originalPrice > 0) {
                return (
                    <span>
                        {sideName} -
                        <span className="discount-original-price">
                            {formatPriceEuro(originalPrice, lang)}
                        </span>
                        <span className="discount-final-price">
                            {formatPriceEuro(discountedPrice, lang)}
                        </span>
                    </span>
                );
            }
        }
        return `${sideName} - ${priceSideName}`;
    })();

    // Create dessert display with crossed-out pricing when discount exists
    const displayDessertName = shouldHideIndividualPrices ? dessertName : (() => {
        if (discountPercentage > 0 && !dessert?.price) {
            const originalPrice = getPriceDessert(portionSize, pricesExtra);
            const discountedPrice = getPriceDessertWithDiscount(portionSize, pricesExtra, discountPercentage);
            if (originalPrice > 0) {
                return (
                    <span>
                        {dessertName} -
                        <span className="discount-original-price">
                            {formatPriceEuro(originalPrice, lang)}
                        </span>
                        <span className="discount-final-price">
                            {formatPriceEuro(discountedPrice, lang)}
                        </span>
                    </span>
                );
            }
        }
        return `${dessertName} - ${priceDessertName}`;
    })();

    const sOrder = useDatabase(date && userOrdersPathDate(uid, date) || null);

    const addMeal = () => {
        const line: Record<string, unknown> = {
            quantity: 1,
            key: mealKey,
            recipient,
            schoolBreak,
            portionSize: portionSize,
        };
        if (isSetMenu) {
            const wantSide = isComboAlways || pendingSideSelected;
            const wantDessert = isComboAlways || pendingDessertSelected;
            if (wantSide && !!sideName) line.quantitySide = 1;
            if (wantDessert && !!dessertName) line.quantityDessert = 1;
        }
        sOrder.ref.child('meals').push(line);
    }

    const setSideChecked = (checked: boolean) => {
        setPendingSideSelected(checked);
        if (quantity > 0) {
            if (checked) {
                if (!quantitySide) qSnapshot?.ref.child('quantitySide').set(quantity);
                else setQuantitySide(quantity);
            } else {
                qSnapshot?.ref.child('quantitySide').remove();
            }
        }
    };

    const setDessertChecked = (checked: boolean) => {
        setPendingDessertSelected(checked);
        if (quantity > 0) {
            if (checked) {
                if (!quantityDessert) qSnapshot?.ref.child('quantityDessert').set(quantity);
                else setQuantityDessert(quantity);
            } else {
                qSnapshot?.ref.child('quantityDessert').remove();
            }
        }
    };
    const addSide = (total: number = 1) => {
        qSnapshot?.ref.child('quantitySide').set(total);
    }
    const addDessert = (total: number = 1) => {
        qSnapshot?.ref.child('quantityDessert').set(total);
    }

    const onSubtract = () => {
        if (quantity > 1) {
            setQuantity(quantity - 1);
            if (quantitySide >= quantity)
                setQuantitySide(quantity - 1);
            if (quantityDessert >= quantity)
                setQuantityDessert(quantity - 1);
        }
        else
            qSnapshot.ref.remove();
    }
    const onSubtractCheckBox = () => {
        if (quantity > 1) {
            setQuantity(quantity - 1);
            if (quantitySide > 0)
                setQuantitySide(quantity - 1);
            if (quantityDessert > 0)
                setQuantityDessert(quantity - 1);
        }
        else
            qSnapshot.ref.remove();
    }
    const onSubtractSide = () => {
        if (quantitySide > 1)
            setQuantitySide(quantitySide - 1);
        else
            qSnapshot.ref.child('quantitySide').remove();
    }
    const onSubtractSideCheckBox = () => {
        qSnapshot.ref.child('quantitySide').remove();
    }
    const onSubtractDessert = () => {
        if (quantityDessert > 1)
            setQuantityDessert(quantityDessert - 1);
        else {
            qSnapshot.ref.child('quantityDessert').remove();
        }
    }
    const onSubtractDessertCheckBox = () => {
        qSnapshot.ref.child('quantityDessert').remove();
    }

    const onAdd = () => {

        if (!quantity) {
            addMeal();
        }
        else {
            if (quantity < MAX_MEAL_ITEMS) {
                setQuantity(quantity + 1);
                if (isSetMenuForStudent) {
                    setQuantitySide(quantitySide + 1);
                    setQuantityDessert(quantityDessert + 1);
                }
            }
        }
    }

    const onAddCheckBox = () => {

        if (!quantity) {
            addMeal();
        }
        else {
            if (quantity < MAX_MEAL_ITEMS) {
                setQuantity(quantity + 1);
                if (quantitySide > 0)
                    setQuantitySide(quantitySide + 1);
                if (quantityDessert > 0)
                    setQuantityDessert(quantityDessert + 1);
            }
        }
    }

    const onAddSide = () => {

        if (!quantitySide)
            addSide();
        else {
            if (quantitySide < quantity)
                setQuantitySide(quantitySide + 1);
        }
    }
    const onAddSideCheckBox = () => {

        if (!quantitySide)
            addSide(quantity);
        else
            setQuantitySide(quantity);
    }

    const onAddDessert = () => {

        if (!quantityDessert)
            addDessert();
        else {
            if (quantityDessert < quantity)
                setQuantityDessert(quantityDessert + 1);
        }
    }
    const onAddDessertCheckBox = () => {
        if (!quantityDessert)
            addDessert(quantity);
        else
            setQuantityDessert(quantityDessert);
    }

    const onPortionChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        if (value === PORTION_SIZE_SMALL.toString()) {
            setCustomPortion(PORTION_SIZE_SMALL);
            if (qSnapshot)
                sOrder.ref.child('meals').child(qSnapshot.key).child('portionSize').set(PORTION_SIZE_SMALL);
        }
        else if (value === PORTION_SIZE_MEDIUM.toString()) {
            setCustomPortion(PORTION_SIZE_MEDIUM);
            if (qSnapshot)
                sOrder.ref.child('meals').child(qSnapshot.key).child('portionSize').set(PORTION_SIZE_MEDIUM);
        }
        else if (value === PORTION_SIZE_NORMAL.toString()) {
            setCustomPortion(PORTION_SIZE_NORMAL);
            if (qSnapshot)
                sOrder.ref.child('meals').child(qSnapshot.key).child('portionSize').set(PORTION_SIZE_NORMAL);
        }
    }

    const menuTitle = isSetMenu
        ? meal.isSide
            ? orderStrings.mealsPopupSideDishLabel
            : meal.isDessert
                ? orderStrings.mealsPopupDessertLabel
                : `${orderStrings.mealsPopupMenuWordLabel} ${meal.menuType}`
        : '';

    const allergensCatalog: Allergens = useDatabase(ALLERGENS_PATH)?.val();
    const mealAllergenLabels = useMemo(
        () => getMealAllergenLabels(meal.allergens, allergensCatalog, lang),
        [meal.allergens, allergensCatalog, lang],
    );
    const hasMealAllergens = mealAllergenLabels.length > 0;
    const hasDietaryAttrs = !!meal.bio || !!meal.vegan || !!meal.glutenFree || !!meal.frozen;

    const portionSelectValue = useMemo((): string => {
        const raw = (!!customPortion && customPortion > 0) ? customPortion :
            portionSize === PORTION_SIZE_SMALL ? (hasSmallPortion ? PORTION_SIZE_SMALL : hasMediumPortion ? PORTION_SIZE_MEDIUM : hasNormalPortion ? PORTION_SIZE_NORMAL : null) :
                portionSize === PORTION_SIZE_MEDIUM ? (hasMediumPortion ? PORTION_SIZE_MEDIUM : hasNormalPortion ? PORTION_SIZE_NORMAL : hasSmallPortion ? PORTION_SIZE_SMALL : null) :
                    portionSize === PORTION_SIZE_NORMAL ? (hasNormalPortion ? PORTION_SIZE_NORMAL : hasMediumPortion ? PORTION_SIZE_MEDIUM : hasSmallPortion ? PORTION_SIZE_SMALL : null) : null;
        return raw != null ? raw.toString() : '';
    }, [customPortion, portionSize, hasSmallPortion, hasMediumPortion, hasNormalPortion]);

    const originalPriceStr = shouldUseComboPrice
        ? getPriceCombo(portionSize, meal, combosData, recipientIsStaff)?.toFixed(2)
        : getPrice(portionSize, meal?.priceSmall, meal?.priceMedium, meal?.price)?.toFixed(2);
    const finalPriceStr = formatPriceEuro(Number(mealPrice ?? 0), lang);
    const titleDimmed = isStaffDiscountable ? !canOrderMealMenu : cannotOrderMealSetMenu;

    return <MealPopupFoodItemLayout
        hideBottomBorder={hideBottomBorder}
        selected={quantity > 0}
        setMenuTitle={isSetMenu ? menuTitle : undefined}
        setMenuTitleDimmed={cannotOrderMealSetMenu}
        titleDimmed={titleDimmed}
        title={getLangValue(meal.name, lang)}
        description={getLangValue(meal.description, lang)}
        dietary={hasDietaryAttrs &&
            <div className="meal-popup-food-item__dietary">
                {!!meal.bio && <img className="meal-attr-icons" src="/img-assets/bio.svg" title={commonStrings.bio} alt="" />}
                {!!meal.vegan && <img className="meal-attr-icons" src="/img-assets/vegan.svg" title={commonStrings.vegetarian} alt="" />}
                {!!meal.glutenFree && <img className="meal-attr-icons" src="/img-assets/gluten-free.svg" title={commonStrings.glutenFree} alt="" />}
                {!!meal.frozen && <img className="meal-attr-icons" src="/img-assets/frozen.svg" title={commonStrings.frozen} alt="" />}
            </div>
        }
        allergens={hasMealAllergens &&
            <div className="meal-popup-food-item__allergens">
                <MealAllergensIndicator labels={mealAllergenLabels} buttonLabel={orderStrings.mealsPopupAllergensLabel} />
            </div>
        }
        checkboxes={isSetMenu &&
            <div className="meal-popup-food-item__checkboxes">
                {!!sideName &&
                    <div className={`popup-title-line-meals ${(isComboAlways || sideSelected) ? 'selected' : ''}`}>
                        <div className={`popup-title ${cannotOrderMealSetMenu && 'opac5' || ''}`} style={{ flex: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        disabled={cannotOrderMealSetMenu || isComboAlways}
                                        checked={isComboAlways || sideSelected}
                                        onChange={(e) => {
                                            if (isComboWhenAnySelected) {
                                                // For "When any selected" mode, both side and dessert should be selected together
                                                setSideChecked(e.target.checked);
                                                if (!!dessertName) {
                                                    setDessertChecked(e.target.checked);
                                                }
                                            } else {
                                                setSideChecked(e.target.checked);
                                            }
                                        }}
                                        sx={{ padding: '4px', color: 'var(--lb_green)', '&.Mui-checked': { color: 'var(--lb_green)' } }}
                                    />
                                }
                                label={displaySideName}
                                sx={{
                                    '& .MuiFormControlLabel-label': { fontSize: '14px', color: 'var(--secondary_text)' },
                                    marginLeft: '0px',
                                }}
                            />
                        </div>
                    </div>
                }

                {!!dessertName &&
                    <div className={`popup-title-line-meals ${(isComboAlways || dessertSelected) ? 'selected' : ''}`}>
                        <div className={`popup-title ${cannotOrderMealSetMenu && 'opac5' || ''}`} style={{ flex: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        disabled={cannotOrderMealSetMenu || isComboAlways}
                                        checked={isComboAlways || dessertSelected}
                                        onChange={(e) => {
                                            if (isComboWhenAnySelected) {
                                                // For "When any selected" mode, both side and dessert should be selected together
                                                setDessertChecked(e.target.checked);
                                                if (!!sideName) {
                                                    setSideChecked(e.target.checked);
                                                }
                                            } else {
                                                setDessertChecked(e.target.checked);
                                            }
                                        }}
                                        sx={{ padding: '4px', color: 'var(--lb_green)', '&.Mui-checked': { color: 'var(--lb_green)' } }}
                                    />
                                }
                                label={displayDessertName}
                                sx={{
                                    '& .MuiFormControlLabel-label': { fontSize: '14px', color: 'var(--secondary_text)' },
                                    marginLeft: '0px',
                                }}
                            />
                        </div>
                    </div>
                }
            </div>
        }
        imageUrl={imageUrl}
        imageAlt={getLangValue(meal.name, lang)}
        footerPrice={isStaffDiscountable &&
            <>
                <span className="meal-popup-food-item__price-old">{originalPriceStr}€</span>
                <span className="meal-popup-food-item__price-final">{finalPriceStr}</span>
            </>
            ||
            (discountPercentage > 0 ? (
                <>
                    <span className="meal-popup-food-item__price-old">{originalPriceStr}€</span>
                    <span className="meal-popup-food-item__price-final">{finalPriceStr}</span>
                    <span className="discount-indicator">🎉 -{discountPercentage}%</span>
                </>
            ) : (
                <span className="meal-popup-food-item__price-final">{finalPriceStr}</span>
            ))
        }
        footerActions={<>
            {shouldShowPortionSizeValue &&
                <FormControl size="small" className="meal-popup-portion-wrap">
                    <Select
                        className="meal-popup-portion-select"
                        value={portionSelectValue}
                        onChange={onPortionChange}
                        disabled={isStaffDiscountable || cannotOrderMealSetMenu || !allowChangePortions}
                        MenuProps={{
                            disableScrollLock: true,
                            slotProps: {
                                root: {
                                    sx: { zIndex: MEALS_POPUP_OVERLAY_Z_INDEX },
                                },
                            },
                        }}
                        sx={{
                            height: 32,
                            minWidth: 80,
                            borderRadius: '8px',
                            backgroundColor: 'var(--order_card_bg)',
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiSelect-select': {
                                py: '8px',
                                pl: '8px',
                                pr: '24px',
                                fontSize: '12px',
                                lineHeight: '14px',
                                letterSpacing: '0.21px',
                                color: 'var(--black_txt_title)',
                            },
                        }}
                    >
                        {hasSmallPortion && <MenuItem value={PORTION_SIZE_SMALL.toString()}>{smallPortionName}</MenuItem>}
                        {hasMediumPortion && <MenuItem value={PORTION_SIZE_MEDIUM.toString()}>{mediumPortionName}</MenuItem>}
                        {hasNormalPortion && <MenuItem value={PORTION_SIZE_NORMAL.toString()}>{normalPortionName}</MenuItem>}
                    </Select>
                </FormControl>
            }
            <div className="meal-popup-counter">
                <button
                    type="button"
                    className="meal-popup-counter__btn"
                    onClick={onSubtractCheckBox}
                    disabled={!quantity || quantity <= 0 || cannotOrderMealSetMenu}
                >-</button>
                <span className={`num-items ${cannotOrderMealSetMenu && 'opac5' || ''}`}>{quantity || 0}</span>
                <button
                    type="button"
                    className="meal-popup-counter__btn"
                    onClick={onAddCheckBox}
                    disabled={quantity >= MAX_MEAL_ITEMS || cannotOrderMealSetMenu}
                >+</button>
            </div>
        </>}
    />;
}
type MealPopUpLineProps = {
    date: string
    mealKey: string
    meal: Meal
    recipient: string
    schoolBreak: string
    qSnapshot: DataSnapshot
    sideDish: Meal
    dessert: Meal
    hideBottomBorder?: boolean
}
